import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ChatSession, MoveToProjectModalResult, RenameChatModalResult } from '../../models/chat.models';
import { AIAgentService } from '../../services/ai-agent.service';
import { DeoOwnershipService } from '../../services/deo-ownership.service';
import { getApiErrorMessage } from '../../services/api-error.helper';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { GainsApplyCancelService } from 'app/shared/services/gains-apply-delete-service/gains-apply-delete.service';
import { DeoAiAssistantRenameChatComponent } from '../deo-ai-assistant-rename-chat/deo-ai-assistant-rename-chat.component';
import { DeoAiAssistantMoveToProjectComponent } from '../deo-ai-assistant-move-to-project/deo-ai-assistant-move-to-project.component';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_DEO_AGENT_PROJECTS, FEATURE_FLAG_DEO_AGENT_RENAME_CHAT } from 'app/shared/services/feature-flags/feature-flags.constants';
import { isTempSessionId } from '../../models/temp-session.constants';
import { AgentRunHistoryEntry } from 'app/modules/agent-library/models/agent-run.model';
import { runDateOf } from '../../services/agent-runs.helper';

@Component({
  selector: 'deo-ai-recent-starred-chat-section',
  standalone: false,
  templateUrl: './deo-ai-recent-starred-chat-section.component.html',
  styleUrls: ['./deo-ai-recent-starred-chat-section.component.scss']
})
export class DeoAiRecentStarredChatSectionComponent implements OnInit {
  @Input() starredSessions: ChatSession[] = [];
  @Input() recentSessions: ChatSession[] = [];
  @Input() currentSessionId: string | null = null;
  /** When the sidebar is scoped to a single project, removing a chat from the project drops it from the list. */
  @Input() isProjectView = false;
  /** Backend failure message from the host's chat-sessions load, or null when it succeeded. */
  @Input() loadError: string | null = null;
  @Input() isLoading = false;

  /**
   * The recorded runs of the agent being talked to, shown instead of the chat lists.
   *
   * `null` means DEO, so the chats belong here; an empty array is an agent with no runs yet, which
   * still replaces them — showing DEO's chats under an agent's name would be a plain lie. A run is
   * not a chat session, so those rows carry no star and no menu: nothing that would ask the backend
   * to rename or delete a session that does not exist.
   */
  @Input() agentRuns: AgentRunHistoryEntry[] | null = null;

  /** The run whose transcript is open, so its row can be marked. */
  @Input() selectedRunId: string | null = null;

  @Output() chatSelected = new EventEmitter<string>();

  @Output() runSelected$ = new EventEmitter<AgentRunHistoryEntry>();
  @Output() chatDeleted$ = new EventEmitter<string>();
  @Output() chatRemovedFromProject$ = new EventEmitter<string>();
  /** Asks the host to re-run the chat-sessions load after a failure. */
  @Output() retry$ = new EventEmitter<void>();

  get isShowingRuns(): boolean {
    return this.agentRuns !== null;
  }

  /** `meta` is pre-formatted as `date · time · who · id`; the date alone is enough to tell two runs apart. */
  runDate(run: AgentRunHistoryEntry): string {
    return runDateOf(run);
  }

  onSelectRun(run: AgentRunHistoryEntry): void {
    this.runSelected$.emit(run);
  }

  protected trackByRunId(index: number, run: AgentRunHistoryEntry): string {
    return run.id;
  }

  /** Drives banner-vs-block: a failure with chats still present is reported above them, not instead of them. */
  get hasChats(): boolean {
    return this.starredSessions.length > 0 || this.recentSessions.length > 0;
  }

  chatMenuItems: EllipsisMenuItem[] = [
    {
      title: 'Rename',
      icon: 'rename.svg',
      id: 'rename'
    },
    {
      title: 'Move to Project',
      icon: 'MoveProject.svg',
      id: 'move-to-project'
    },
    {
      title: 'Remove from Project',
      icon: 'MoveProject.svg',
      id: 'remove-from-project'
    },
    {
      title: 'Delete',
      icon: 'trash-2.svg',
      id: 'delete',
      warning: true,
      divider: true
    }
  ];
  constructor(
    private readonly _aiAgentService: AIAgentService,
    private readonly _gainsToastService: GainsToastService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _modalService: NzModalService,
    private readonly _gainsApplyCancelService: GainsApplyCancelService,
    private readonly _ownershipService: DeoOwnershipService
  ) {}

  /** A chat another user keeps in a shared project can be opened and read, never changed. */
  canManage(session: ChatSession): boolean {
    return this._ownershipService.isMine(session.user_id);
  }

  ngOnInit(): void {
    if (!this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_RENAME_CHAT)) {
      this.chatMenuItems = this.chatMenuItems.filter(item => item.title !== 'Rename');
    }

    if (!this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_PROJECTS)) {
      this.chatMenuItems = this.chatMenuItems.filter(item => item.title !== 'Move to Project' && item.title !== 'Remove from Project');
    }

    if (this.chatMenuItems[0]?.divider) {
      this.chatMenuItems = [{ ...this.chatMenuItems[0], divider: false }, ...this.chatMenuItems.slice(1)];
    }
  }

  /** The sessions payload carries the artifact total (PLAN-2188), so the session itself is the source. */
  getArtifactCount(session: ChatSession): number {
    return session.artifactCount ?? 0;
  }

  onSelectChat(sessionId: string): void {
    this.chatSelected.emit(sessionId);
  }

  onToggleStar(sessionId: string, event: Event): void {
    event.stopPropagation();

    const session = this.starredSessions.find(s => s.id === sessionId) || this.recentSessions.find(s => s.id === sessionId);
    if (session) {
      session.isFavorite = !session.isFavorite;
      this._aiAgentService.updateFavorite(sessionId, session.isFavorite);
    }
  }

  onRenameChat(sessionId: string, event: Event): void {
    event.stopPropagation();

    const session = this.starredSessions.find(s => s.id === sessionId) || this.recentSessions.find(s => s.id === sessionId);
    if (session) {
      this.openRenameChatModal(sessionId, session.title);
    }
  }

  private openRenameChatModal(sessionId: string, originalChatName: string): void {
    const modalRef = this._modalService.create({
      nzContent: DeoAiAssistantRenameChatComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzData: { sessionId, originalChatName },
      nzBodyStyle: { padding: '0' }
    });

    modalRef.afterClose.subscribe((result: RenameChatModalResult | undefined) => {
      if (!result?.newChatName) {
        return;
      }
      this.renameChatSession(result.sessionId, result.newChatName, originalChatName);
    });
  }

  private renameChatSession(sessionId: string, newChatName: string, originalChatName: string): void {
    this._aiAgentService.renameSession(sessionId, newChatName).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to rename chat. Please try again.');
          return;
        }
        this.renameChatInLists(sessionId, newChatName);
        this._gainsToastService.success('Chat Renamed', `The chat <strong>"${originalChatName}"</strong> has been renamed to <strong>"${newChatName}"</strong>.`);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to rename chat. Please try again.'));
      }
    });
  }

  private renameChatInLists(sessionId: string, newChatName: string): void {
    const session = this.starredSessions.find(s => s.id === sessionId) || this.recentSessions.find(s => s.id === sessionId);
    if (session) {
      session.title = newChatName;
    }
  }

  onMoveChatToProject(session: ChatSession, event: Event): void {
    event.stopPropagation();
    if (!session) {
      return;
    }
    this.openMoveToProjectModal(session);
  }

  private openMoveToProjectModal(session: ChatSession): void {
    const modalRef = this._modalService.create({
      nzContent: DeoAiAssistantMoveToProjectComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzData: { sessionCount: 1, currentProjectId: session.projectId ?? null },
      nzBodyStyle: { padding: '0' }
    });

    modalRef.afterClose.subscribe((result: MoveToProjectModalResult | undefined) => {
      if (!result) {
        return;
      }
      this.moveChatToProject(session, result.projectId);
    });
  }

  private moveChatToProject(session: ChatSession, projectId: string | null): void {
    const previousProjectId = session.projectId ?? null;
    this._aiAgentService.setSessionProject(session.id, projectId).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to move chat. Please try again.');
          return;
        }
        session.projectId = projectId;
        // Inside a project view, a chat moved to a different project leaves this project's list.
        if (this.isProjectView && projectId !== previousProjectId) {
          this.removeSessionLocally(session.id);
          this.chatRemovedFromProject$.emit(session.id);
        }
        this._gainsToastService.success('Chat Moved', `The chat <strong>"${session.title}"</strong> has been moved to the selected project.`);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to move chat. Please try again.'));
      }
    });
  }

  onRemoveChatFromProject(session: ChatSession, event: Event): void {
    event.stopPropagation();
    if (!session) {
      return;
    }
    this.removeChatFromProject(session);
  }

  private removeChatFromProject(session: ChatSession): void {
    // Temp chats are not persisted (and not linked to a project), so just detach locally.
    if (isTempSessionId(session.id)) {
      this.applyRemovedFromProject(session);
      return;
    }

    this._aiAgentService.setSessionProject(session.id, null).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to remove chat from project. Please try again.');
          return;
        }
        this.applyRemovedFromProject(session);
        this._gainsToastService.success('Chat Removed', `The chat <strong>"${session.title}"</strong> has been removed from the project.`);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to remove chat from project. Please try again.'));
      }
    });
  }

  private applyRemovedFromProject(session: ChatSession): void {
    session.projectId = null;
    // Inside a project view the chat no longer belongs, so drop the row immediately for an instant refresh.
    if (this.isProjectView) {
      this.removeSessionLocally(session.id);
    }
    this.chatRemovedFromProject$.emit(session.id);
  }

  onDeleteChat(sessionId: string, event: Event): void {
    event.stopPropagation();

    const session = this.starredSessions.find(s => s.id === sessionId) || this.recentSessions.find(s => s.id === sessionId);
    if (!session) return;

    this._gainsApplyCancelService.delete(
      'Delete Chat',
      `<div><div>Are you sure you want to delete chat "${session.title}"? This action cannot be undone.</div><div class="padding-top-10">Are you sure you want to proceed?</div></div>`,
      () => this.deleteChat(session),
      () => {
        // Cancel action
      }
    );
  }

  private deleteChat(session: ChatSession): void {
    // Temporary sessions are not yet saved to the backend, so just remove them locally.
    if (isTempSessionId(session.id)) {
      this.removeSessionLocally(session.id);
      this.chatDeleted$.emit(session.id);
      this._gainsToastService.success('Success', 'Chat deleted successfully');
      return;
    }

    this._aiAgentService.deleteChatSession(session.id).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to delete chat');
          return;
        }
        this.removeSessionLocally(session.id);
        this.chatDeleted$.emit(session.id);
        this._gainsToastService.success('Success', 'Chat deleted successfully');
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to delete chat'));
      }
    });
  }

  /** Optimistically drop the row so the sidebar refreshes immediately; the host stays in sync via chatDeleted$. */
  private removeSessionLocally(sessionId: string): void {
    this.starredSessions = this.starredSessions.filter(session => session.id !== sessionId);
    this.recentSessions = this.recentSessions.filter(session => session.id !== sessionId);
  }

  onChatMenuAction(action: string, chat: ChatSession): void {
    const menuActionEvent = new Event('menu-action');

    if (action === 'Rename') {
      this.onRenameChat(chat.id, menuActionEvent);
    }

    if (action === 'Move to Project') {
      this.onMoveChatToProject(chat, menuActionEvent);
    }

    if (action === 'Remove from Project') {
      this.onRemoveChatFromProject(chat, menuActionEvent);
    }

    if (action === 'Delete') {
      this.onDeleteChat(chat.id, menuActionEvent);
    }
  }
}
